import React, { useState, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import CellTemplate from '../../components/canvas/CellTemplate';
import RectangleShape from '../../components/shapes/Rectangle/RectangleShape';
import CircleShape from '../../components/shapes/Circle/CircleShape';
import ArcShape from '../../components/shapes/Arc/ArcShape';
import ArrowShape from '../../components/shapes/Arrow/ArrowShape';
import ImageShape from '../../components/shapes/Image/ImageShape';
import TextShape from '../../components/shapes/Text/TextShape';
import LineShape from '../../components/shapes/Line/LineShape';
import TagShape from '../../components/shapes/Tag/TagShape';
import StarShape from '../../components/shapes/Star/StarShape';
import RingShape from '../../components/shapes/Ring/RingShape';
import { substituteVariables } from '../../utils/variableSubstitution';
import { isEnhancedCell, buildEnhancedShapes, getProductSlotBounds } from '../../utils/productBlockLayout';

const GridItem = ({index, cell, cell_width, cell_height, spanWidth, spanHeight, originalTemplateWidth, originalTemplateHeight, assignTemplate, selectedTemplate, handleCellUpdate, nextProductSlot, updateLayerRefs, flyerData, cellMode, overrideType, overrideContent, onCellOverride, cellData, selectedCellProduct, onSelectCellProduct, onDeleteCellProduct}) => {
    const [borderStyle, setBorderStyle] = useState('none');
    const [isTemplateAssigned, setIsTemplateAssigned] = useState(false);
    const layerRef = React.useRef();

    // Context menu state for cell overrides
    const [contextMenu, setContextMenu] = useState(null);
    const [showOverrideEditor, setShowOverrideEditor] = useState(false);
    const [overrideEditorType, setOverrideEditorType] = useState('brand_ad');
    const [overrideLabel, setOverrideLabel] = useState('');
    const [overrideImageUrl, setOverrideImageUrl] = useState('');
    const contextMenuRef = useRef(null);

    const handleDeleteProduct = (productIndex) => {
      if (onDeleteCellProduct) {
        onDeleteCellProduct(index, productIndex);
      }
    };

    // Close context menu on outside click
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
          setContextMenu(null);
        }
      };
      if (contextMenu) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [contextMenu]);

    const [{ isOver, dragItem }, drop] = useDrop(() => ({

        accept: ['template', 'product'], // now it can also accept 'product'
        drop: async (item) => {
          let hasCellBeenUpdated = false;

          // Enhanced cell: store product data and let the layout engine render
          if (item.type === 'product' && cellData && isEnhancedCell(cellData)) {
            const cellArea = (cellData.spanWidth || 1) * (cellData.spanHeight || 1);
            const maxProducts = Math.min(cellData.product_block?.max_products || 1, cellArea);
            const existingProducts = cellData.products || [];
            if (existingProducts.length >= maxProducts) return;
            const newProduct = {
              name: item.name,
              promoPrice: item.promoPrice,
              originalPrice: item.originalPrice,
              priceUnit: item.priceUnit,
              productDescription: item.productDescription,
              limit_qty: item.limit_qty,
              promoType: item.promoType,
              cleanedImgLargeSrc: item.cleanedImgLargeSrc,
              originalImgLargeSrc: item.originalImgLargeSrc,
            };
            handleCellUpdate(index, cell, { products: [...existingProducts, newProduct] });
            return;
          }

          if (item.type === 'product' && cell && Array.isArray(cell)) {
            // Product is dropped, find and update the appropriate TextShape
            let newCell = [...cell]; // make a copy to avoid mutating the prop directly
            let availableProductSlots = [
              ...new Set(
                newCell
                  .filter(shape => shape.linkedProductProperty) // Only include shapes with linkedProductProperty
                  .map(shape => shape.linkedProductNumber)
              )
            ].sort(); 
  
            // Reset product number if it is not valid anymore
            let newProductNumber;
            let currentProductNumber;
            const [, idxNumber] = index.split('-').map(num => parseInt(num));

            if (availableProductSlots.length && availableProductSlots.includes(nextProductSlot[idxNumber].toString())) {
              currentProductNumber = nextProductSlot[idxNumber];
              let currentProductIndex = availableProductSlots.indexOf(nextProductSlot[idxNumber]);
              if (currentProductIndex < availableProductSlots.length - 1) {
                newProductNumber = availableProductSlots[currentProductIndex + 1].toString();
              } else {
                newProductNumber = availableProductSlots[0].toString();
              }
              nextProductSlot[idxNumber] = newProductNumber;
              newCell.forEach((shape, i) => {

                if (shape.type === 'text' && shape.linkedProductNumber && shape.linkedProductNumber.toString() === currentProductNumber.toString()) {
                  if (shape.linkedProductProperty === 'promo_price_dollars') {
                    newCell[i].text = item.promoPrice ? `${Math.floor(item.promoPrice)}` : `${Math.floor(item.originalPrice)}`;
                  } else if (shape.linkedProductProperty === 'promo_price_cents') {
                    let price = item.promoPrice ? item.promoPrice : item.originalPrice;
                    let numPrice = Number(price);
                    if (!isNaN(numPrice)) {
                      let [, cents] = numPrice.toFixed(2).split('.');
                      newCell[i].text = cents;
                    }else {
                      console.error('price is not a number:', price);
                      // Handle this case as needed
                    }
  
                  } else if(shape.linkedProductProperty === 'original_price') {
                    newCell[i].text = item.originalPrice.toString();
                  } else if (shape.linkedProductProperty === 'product_name') {
                    newCell[i].text = item.name;
                  } else if (shape.linkedProductProperty === 'price_unit') {
                    newCell[i].text = item.priceUnit ?  "per " + item.priceUnit : "";
                  } else if (shape.linkedProductProperty === 'product_description') {
                    newCell[i].text = item.productDescription;
                  } else if (shape.linkedProductProperty === 'savings') {
                    newCell[i].text = (item.originalPrice - item.promoPrice).toFixed(2).toString();
                  } else if (shape.linkedProductProperty === 'limited_qty') {
                    if(item.limit_qty) {
                      newCell[i].text = "LIMIT OF " + item.limit_qty.toString();
                    } else {
                      newCell[i].text = "";
                    }
                  } else if (shape.linkedProductProperty === 'promo_type') {
                    if(item.promoType === 'BOGO') {
                      newCell[i].text = "Buy 1 Get 1 Free"
                    } else {
                      newCell[i].text = item.promoType;
                    }
                  }
                }


                if (shape.type === 'image' && shape.linkedProductNumber && shape.linkedProductNumber.toString() === currentProductNumber.toString()) {
                  // Determine which image to use based on linkedProductProperty
                  let imageSrc = null;
                  if (shape.linkedProductProperty === 'cleaned_img_large_src') {
                    // Use cleaned image if available, fallback to original
                    imageSrc = item.cleanedImgLargeSrc || item.originalImgLargeSrc;
                  } else if (shape.linkedProductProperty === 'original_img_large_src') {
                    imageSrc = item.originalImgLargeSrc;
                  }

                  if (imageSrc) {
                    const img = new window.Image();
                    img.crossOrigin = 'anonymous';
                    // Append cache-bust param so the browser doesn't reuse a
                    // non-CORS cached copy from the <img> tag in search results
                    const corsUrl = imageSrc + (imageSrc.includes('?') ? '&' : '?') + 'canvas=1';
                    img.src = corsUrl;
                    img.onload = () => {
                      newCell[i].imageObj = img;
                      newCell[i].imageUrl = imageSrc;
                      handleCellUpdate(index, newCell);
                      hasCellBeenUpdated = true;
                    };
                    img.onerror = () => {
                      console.error(`Failed to load image for shape ${i}: ${imageSrc}`);
                    };
                  } else {
                    console.error(`No image available for shape ${i}, linkedProductProperty: ${shape.linkedProductProperty}`);
                  }
                }
                
              });
              // use handleCellUpdate prop to request the cell update in the parent component
            } 

            if(!hasCellBeenUpdated) handleCellUpdate(index, newCell);
          } else if (item.type === 'template') {
            // It's a template, assign it as before
            assignTemplate(item.id);
            setBorderStyle('none');
            setIsTemplateAssigned(true);
          }
        },
        collect: (monitor) => ({
          canDrop: monitor.canDrop(),
          isOver: monitor.isOver(),
          dragItem: monitor.getItem(),
        }),
    }), [cell, cellData, index, handleCellUpdate, assignTemplate, nextProductSlot]);

    // Effect to reset border style when a template is assigned
    useEffect(() => {
        if (isTemplateAssigned) {
            setBorderStyle('none');
        }
    }, [isTemplateAssigned]);

    // Effect to update layer references
    useEffect(() => {
        if (layerRef.current) {
            updateLayerRefs(index, layerRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateLayerRefs, index]);

    // Function to scale shapes based on cell size
    const scaleShape = (shape, cell_width, cell_height) => {
    // Guard against division by zero or undefined values
    if (!originalTemplateWidth || !originalTemplateHeight || !cell_width || !cell_height) {
      return { ...shape, draggable: false };
    }

    const scale_x = cell_width / originalTemplateWidth;
    const scale_y = cell_height / originalTemplateHeight;

    // Guard against NaN or Infinity
    if (!isFinite(scale_x) || !isFinite(scale_y)) {
      return { ...shape, draggable: false };
    }

        const scaledShape = {...shape};
    // For all shapes
    if (shape.x) scaledShape.x *= scale_x;
    if (shape.y) scaledShape.y *= scale_y;

    if (shape.strokeWidth) scaledShape.strokeWidth *= scale_y;

  
    // For shapes with width and height (rectangle, image)
    if  (shape.width) scaledShape.width *= scale_x;
    if (shape.height) scaledShape.height *= scale_y;
  
    // For circular shapes (circle, arc)
    if (shape.radius) scaledShape.radius *= scale_x;

    // For rounded rectangles
    if (shape.cornerRadius) scaledShape.cornerRadius *= scale_x;

    // For arc
    if (shape.innerRadius) scaledShape.innerRadius *= scale_x;
    if (shape.outerRadius) scaledShape.outerRadius *= scale_x;
  
    // For arrow
    if (shape.points) {
      scaledShape.points = shape.points.map(point => point * scale_x);
    }
  
    // For text
    if (shape.fontSize) {
      scaledShape.fontSize *=  scale_x;
    }  
    // New scaling properties for text  
    if (!shape.fontSize && shape.lineHeight) scaledShape.lineHeight *= scale_y;
    if (!shape.fontSize && shape.padding) scaledShape.padding *= scale_x;

    scaledShape.draggable = false;
    return scaledShape;
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      if (onCellOverride) {
        setContextMenu({ x: e.clientX, y: e.clientY });
      } else {
        assignTemplate(index, selectedTemplate);
      }
    };

    const handleInsertOverride = (type) => {
      setContextMenu(null);
      setOverrideEditorType(type);
      setOverrideLabel('');
      setOverrideImageUrl('');
      setShowOverrideEditor(true);
    };

    const handleSaveOverride = () => {
      if (onCellOverride) {
        onCellOverride(index, {
          cellMode: 'override',
          overrideType: overrideEditorType,
          overrideContent: {
            label: overrideLabel,
            imageUrl: overrideImageUrl,
          }
        });
      }
      setShowOverrideEditor(false);
    };

    const handleClearOverride = () => {
      setContextMenu(null);
      if (onCellOverride) {
        onCellOverride(index, { cellMode: 'product' });
      }
    };

    // If cell is in override mode, render the override content
    const isOverrideMode = cellMode === 'override';

    // Compute drop hint for the overlay
    const getDropHint = () => {
      if (!isOver || !dragItem || dragItem.type !== 'product') return null;

      // Enhanced cell
      if (cellData && isEnhancedCell(cellData)) {
        const cellArea = (cellData.spanWidth || 1) * (cellData.spanHeight || 1);
        const maxProducts = Math.min(cellData.product_block?.max_products || 1, cellArea);
        const existingProducts = cellData.products || [];
        const count = existingProducts.length;
        if (count >= maxProducts) {
          return { label: 'Cell full', style: 'full' };
        }
        return { label: `Add product (${count + 1}/${maxProducts})`, style: 'add' };
      }

      // Legacy cell
      if (cell && Array.isArray(cell)) {
        const [, idxNumber] = index.split('-').map(num => parseInt(num));
        const availableSlots = [
          ...new Set(
            cell
              .filter(s => s.linkedProductProperty)
              .map(s => s.linkedProductNumber)
          )
        ].sort();
        if (availableSlots.length > 0) {
          const currentSlot = nextProductSlot?.[idxNumber];
          return { label: `Set product #${currentSlot}`, style: 'add' };
        }
      }

      // No template assigned
      return { label: 'Drop product', style: 'add' };
    };

    const dropHint = getDropHint();

    return (
        <div
            className='grid-item'
            ref={drop}
            style={{
              display: cell_width === 0 ? 'none' : 'block',
              width: cell_width,
              height: cell_height,
              gridColumn: 'span ' + spanWidth,
              gridRow: 'span ' + spanHeight,
              border: (cellData?.border_color && cellData?.border_width) ? `${cellData.border_width}px solid ${cellData.border_color}` : borderStyle,
              borderRadius: cellData?.border_radius ? cellData.border_radius + 'px' : undefined,
              backgroundColor: cellData?.background_color || 'white',
              padding: cellData?.cell_padding ? cellData.cell_padding + 'px' : undefined,
              position: 'relative',
              overflow: 'hidden'
            }}
            key={index}
            onContextMenu={handleContextMenu}
            onClick={(e) => {
              // Clear product selection when clicking cell background
              if (e.target === e.currentTarget) {
                if (onSelectCellProduct) onSelectCellProduct(null, null);
              }
            }}
        >
            {/* Drop Zone Overlay */}
            {dropHint && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                pointerEvents: 'none',
                backgroundColor: dropHint.style === 'full' ? 'rgba(220,38,38,0.12)' :
                  dropHint.style === 'replace' ? 'rgba(245,158,11,0.12)' :
                  'rgba(34,197,94,0.12)',
                border: `2px dashed ${
                  dropHint.style === 'full' ? '#dc2626' :
                  dropHint.style === 'replace' ? '#f59e0b' :
                  '#22c55e'
                }`,
                borderRadius: cellData?.border_radius ? cellData.border_radius + 'px' : undefined,
              }}>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: dropHint.style === 'full' ? '#dc2626' :
                    dropHint.style === 'replace' ? '#f59e0b' :
                    '#22c55e',
                  whiteSpace: 'nowrap',
                }}>
                  {dropHint.label}
                </span>
              </div>
            )}

            {/* Context Menu */}
            {contextMenu && (
              <div
                ref={contextMenuRef}
                className="cell-context-menu"
                style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 1000 }}
              >
                <button onClick={() => handleInsertOverride('brand_ad')}>Insert Brand Ad</button>
                <button onClick={() => handleInsertOverride('offer')}>Insert Offer</button>
                {isOverrideMode && (
                  <button onClick={handleClearOverride}>Clear Override</button>
                )}
              </div>
            )}

            {/* Override Editor Modal */}
            {showOverrideEditor && (
              <div className="cell-override-editor" style={{ position: 'absolute', top: 0, left: '100%', zIndex: 1001 }}>
                <div className="override-editor-content">
                  <h4>{overrideEditorType === 'brand_ad' ? 'Brand Ad' : 'Offer'}</h4>
                  <label>
                    Label
                    <input
                      type="text"
                      value={overrideLabel}
                      onChange={(e) => setOverrideLabel(e.target.value)}
                      placeholder={overrideEditorType === 'brand_ad' ? 'e.g., Coca-Cola' : 'e.g., 30% off toothpaste'}
                    />
                  </label>
                  <label>
                    Image URL
                    <input
                      type="text"
                      value={overrideImageUrl}
                      onChange={(e) => setOverrideImageUrl(e.target.value)}
                      placeholder="https://..."
                    />
                  </label>
                  <div className="override-editor-actions">
                    <button onClick={() => setShowOverrideEditor(false)}>Cancel</button>
                    <button onClick={handleSaveOverride} className="save-btn">Save</button>
                  </div>
                </div>
              </div>
            )}

            {/* Override Mode Rendering */}
            {isOverrideMode && overrideContent && (
              <div className="cell-override-display" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className={`cell-override-badge ${overrideType}`}>
                  {overrideType === 'brand_ad' ? 'Brand Ad' : 'Offer'}
                </div>
                {overrideContent.imageUrl && (
                  <img
                    src={overrideContent.imageUrl}
                    alt={overrideContent.label || 'Override'}
                    style={{ maxWidth: '90%', maxHeight: '60%', objectFit: 'contain' }}
                  />
                )}
                {overrideContent.label && (
                  <span className="cell-override-label">{overrideContent.label}</span>
                )}
              </div>
            )}

            {/* Enhanced Template Rendering (three-layer system) */}
            {!isOverrideMode && cellData && isEnhancedCell(cellData) && (
                <CellTemplate
                    width={cell_width}
                    height={cell_height}
                    backgroundColor={cellData?.background_color || 'white'}
                    borderColor={cellData?.border_color}
                    borderWidth={cellData?.border_width || 0}
                    borderRadius={cellData?.border_radius || 0}
                    ref={layerRef}
                >
                    {buildEnhancedShapes(cellData, cellData.products || []).map((shape) => {
                        const scaledShape = scaleShape(shape, cell_width, cell_height);
                        return renderShape(scaledShape, shape, flyerData);
                    })}
                </CellTemplate>
            )}

            {/* Product click overlay — sits on top of everything, handles click-to-select */}
            {!isOverrideMode && cellData && isEnhancedCell(cellData) && cellData.products?.length > 0 && (
              <div
                style={{ position: 'absolute', inset: 0, zIndex: 9999, cursor: 'pointer' }}
                onClick={(e) => {
                  // Determine which product slot was clicked based on click position
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const clickY = e.clientY - rect.top;
                  const slots = getProductSlotBounds(cellData, cell_width, cell_height);
                  // Find the topmost (last rendered) slot that contains the click point
                  let hitSlot = null;
                  for (let i = slots.length - 1; i >= 0; i--) {
                    const s = slots[i];
                    if (clickX >= s.x && clickX <= s.x + s.w && clickY >= s.y && clickY <= s.y + s.h) {
                      hitSlot = s;
                      break;
                    }
                  }
                  if (hitSlot) {
                    if (onSelectCellProduct) onSelectCellProduct(index, hitSlot.productIndex);
                  } else {
                    if (onSelectCellProduct) onSelectCellProduct(null, null);
                  }
                }}
              >
                {/* Visual selection indicators */}
                {getProductSlotBounds(cellData, cell_width, cell_height).map((slot) => {
                  const isSelected = selectedCellProduct?.cellIndex === index && selectedCellProduct?.productIndex === slot.productIndex;
                  return (
                    <div key={`slot-${slot.productIndex}`} style={{
                      position: 'absolute', left: slot.x, top: slot.y, width: slot.w, height: slot.h,
                      border: isSelected ? '2px solid #ff8264' : 'none',
                      borderRadius: 4, boxSizing: 'border-box', pointerEvents: 'none',
                    }}>
                      {isSelected && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteProduct(slot.productIndex); }}
                            style={{
                              position: 'absolute', top: 4, right: 4,
                              background: '#ff8264', color: '#fff', border: 'none',
                              borderRadius: 4, width: 22, height: 22, padding: 0,
                              cursor: 'pointer', pointerEvents: 'auto',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }}
                            title="Remove product"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Normal Product Mode Rendering (legacy flat shapes) */}
            {!isOverrideMode && cell && !(cellData && isEnhancedCell(cellData)) && (
                <CellTemplate
                    width={cell_width}
                    height={cell_height}
                    backgroundColor={cellData?.background_color || 'white'}
                    borderColor={cellData?.border_color}
                    borderWidth={cellData?.border_width || 0}
                    borderRadius={cellData?.border_radius || 0}
                    ref={layerRef}
                >
                    {cell.map((shape) => {
                        const scaledShape = scaleShape(shape, cell_width, cell_height);
                        return renderShape(scaledShape, shape, flyerData);
                    })}
                </CellTemplate>
            )}
        </div>
    );

};

// Helper to render a shape by type (shared between legacy and enhanced paths)
function renderShape(scaledShape, originalShape, flyerData) {
    switch (scaledShape.type) {
        case 'rectangle':
            return <RectangleShape {...scaledShape} key={scaledShape.id} />;
        case 'circle':
            return <CircleShape {...scaledShape} key={scaledShape.id} />;
        case 'arc':
            return <ArcShape {...scaledShape} key={scaledShape.id} />;
        case 'arrow':
            return <ArrowShape {...scaledShape} key={scaledShape.id} />;
        case 'image':
            return <ImageShape {...scaledShape} imageObj={originalShape.imageObj} key={scaledShape.id} />;
        case 'text':
            const substitutedText = flyerData
              ? substituteVariables(scaledShape.text, flyerData)
              : scaledShape.text;
            return <TextShape {...scaledShape} text={substitutedText} key={scaledShape.id} />;
        case 'line':
            return <LineShape {...scaledShape} key={scaledShape.id} />;
        case 'tag':
            return <TagShape {...scaledShape} key={scaledShape.id} />;
        case 'star':
            return <StarShape {...scaledShape} key={scaledShape.id} />;
        case 'ring':
            return <RingShape {...scaledShape} key={scaledShape.id} />;
        default:
            return null;
    }
}

export default GridItem;